import React, { useCallback, useId, useRef, useState } from "react";
import { cn } from "../../lib/cn";

export type TabItem = {
  key: string;
  label: React.ReactNode;
  /** Optional count shown as a pill after the label. */
  count?: number;
  content: React.ReactNode;
};

export type TabsProps = {
  items: TabItem[];
  defaultActiveKey?: string;
  className?: string;
};

/**
 * Accessible tabs with a sliding underline.
 *
 * The indicator is a per-tab element rather than one absolutely-positioned bar,
 * so it needs no measurement and stays correct when labels wrap or the list
 * scrolls.
 */
export function Tabs({ items, defaultActiveKey, className }: TabsProps) {
  const baseId = useId();
  const [active, setActive] = useState(
    () => defaultActiveKey ?? items[0]?.key ?? "",
  );
  const tabRefs = useRef(new Map<string, HTMLButtonElement>());

  const activeIndex = Math.max(
    0,
    items.findIndex((item) => item.key === active),
  );

  const focusTab = useCallback((key: string) => {
    setActive(key);
    tabRefs.current.get(key)?.focus();
  }, []);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (items.length === 0) return;

    let nextIndex: number | null = null;
    if (event.key === "ArrowRight") nextIndex = (activeIndex + 1) % items.length;
    else if (event.key === "ArrowLeft")
      nextIndex = (activeIndex - 1 + items.length) % items.length;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = items.length - 1;

    if (nextIndex === null) return;
    event.preventDefault();
    focusTab(items[nextIndex].key);
  };

  const activeItem = items[activeIndex];

  return (
    <div className={cn("flex min-h-0 flex-col", className)}>
      <div
        role="tablist"
        aria-label="Sections"
        onKeyDown={onKeyDown}
        className="border-line flex shrink-0 gap-0.5 overflow-x-auto border-b"
      >
        {items.map((item) => {
          const isActive = item.key === active;
          return (
            <button
              key={item.key}
              ref={(el) => {
                if (el) tabRefs.current.set(item.key, el);
                else tabRefs.current.delete(item.key);
              }}
              type="button"
              role="tab"
              id={`${baseId}-tab-${item.key}`}
              aria-selected={isActive}
              aria-controls={`${baseId}-panel-${item.key}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActive(item.key)}
              className={cn(
                "relative flex shrink-0 items-center gap-1.5 px-3 py-2.5",
                "text-[13px] font-medium whitespace-nowrap",
                "transition-colors duration-(--duration-instant) ease-(--ease-standard)",
                isActive ? "text-accent" : "text-ink-muted hover:text-ink",
              )}
            >
              {item.label}
              {typeof item.count === "number" && item.count > 0 && (
                <span
                  className={cn(
                    "rounded-full px-1.5 py-px text-[11px] font-semibold tabular-nums",
                    isActive
                      ? "bg-accent-soft text-accent"
                      : "bg-surface-sunken text-ink-subtle",
                  )}
                >
                  {item.count}
                </span>
              )}

              <span
                aria-hidden="true"
                className={cn(
                  "bg-accent absolute inset-x-2 -bottom-px h-0.5 rounded-t origin-center",
                  "transition-transform duration-(--duration-base) ease-(--ease-standard)",
                  isActive ? "scale-x-100" : "scale-x-0",
                )}
              />
            </button>
          );
        })}
      </div>

      {activeItem && (
        <div
          key={activeItem.key}
          role="tabpanel"
          id={`${baseId}-panel-${activeItem.key}`}
          aria-labelledby={`${baseId}-tab-${activeItem.key}`}
          tabIndex={0}
          className="animate-fade-in min-h-0 flex-1 pt-5 focus:outline-none"
        >
          {activeItem.content}
        </div>
      )}
    </div>
  );
}

export default Tabs;
